import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import { exportFormatValues } from '@/common/constants'
import useWebsocketAction from '@/common/hooks/useWebsocketAction/useWebsocketAction'
import { exportTemplateValues } from '@/inventory/pages/InventoryPage/ImportInventoryDataDrawer/ExportInventorySection/constants'
import { progressAnimation } from '@/inventory/pages/InventoryPage/ImportInventoryDataDrawer/ImportProgressModal/constants'
import inventoryServices from '@/inventory/services/services'

import { minimumProgressDurationInMs } from './constants'
import { getProgressClosingDelay, wait } from './helpers'
import {
  transformRequestExportInventoryItems,
  transformRequestImportInventoryFile,
  transformResponseInventoryImportReport,
  transformResponseInventoryImportResult,
} from './transformers'

const useImportInventoryData = ({
  storeId,
  userEmail,
  setToastInfo,
  getFilters,
  refetchItems,
  fetchInventoryMetrics,
}) => {
  const { t } = useTranslation()
  const [showImportInventoryDataDrawer, setShowImportInventoryDataDrawer] = useState(false)
  const [inventoryFile, setInventoryFile] = useState(null)
  const [inventoryError, setInventoryError] = useState('')
  const [exportEmails, setExportEmails] = useState(userEmail ? [userEmail] : [])
  const [exportTemplate, setExportTemplate] = useState(exportTemplateValues.globalInventory)
  const [exportFormat, setExportFormat] = useState(exportFormatValues.xlsx)
  const [exportedInventory, setExportedInventory] = useState(false)
  const [importInProgress, setImportInProgress] = useState(false)
  const [importCompleted, setImportCompleted] = useState(false)
  const [importWebsocketUrl, setImportWebsocketUrl] = useState(null)
  const [showImportResultModal, setShowImportResultModal] = useState(false)
  const importCancelledRef = useRef(false)
  const importReportIdRef = useRef(null)
  const importStartedAtRef = useRef(null)
  const { fetchData: exportInventoryItems, loading: exportingInventory } = useFetch(
    inventoryServices.exportInventoryItems,
    {
      doInitialRequest: false,
      transformRequest: transformRequestExportInventoryItems,
    }
  )
  const { fetchData: importInventoryFile, loading: uploading } = useFetch(inventoryServices.importInventoryItems, {
    doInitialRequest: false,
    transformRequest: transformRequestImportInventoryFile,
    transformResponse: transformResponseInventoryImportReport,
  })
  const { data: importResult, fetchData: getInventoryImport } = useFetch(inventoryServices.getInventoryImport, {
    doInitialRequest: false,
    transformResponse: transformResponseInventoryImportResult,
  })

  const openImportInventoryDataDrawer = () => {
    setShowImportInventoryDataDrawer(true)
  }

  const closeImportInventoryDataDrawer = () => {
    setShowImportInventoryDataDrawer(false)
    setInventoryError('')
    setInventoryFile(null)
  }

  const onSelectInventoryFile = files => {
    setInventoryFile(files?.[0] || null)
  }

  const onDeleteInventoryFile = fileName => {
    if (inventoryFile?.name === fileName) {
      setInventoryFile(null)
    }
  }

  const onExportInventoryItems = async () => {
    const filters = getFilters()
    try {
      await exportInventoryItems({
        storeId,
        filters,
        emails: exportEmails,
        template: exportTemplate,
        format: exportFormat,
      })
      setExportedInventory(true)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
      setExportedInventory(false)
    }
  }

  const onImportCompleted = async () => {
    setImportWebsocketUrl(null)
    setImportCompleted(true)

    try {
      await getInventoryImport({ storeId, reportId: importReportIdRef.current })
      await wait(
        getProgressClosingDelay(
          importStartedAtRef.current,
          minimumProgressDurationInMs,
          progressAnimation.completedDurationInSeconds
        )
      )

      setImportInProgress(false)
      setShowImportResultModal(true)
    } catch (error) {
      setImportInProgress(false)
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const onImportFailed = () => {
    setImportWebsocketUrl(null)
    setImportInProgress(false)
    setToastInfo({ show: true, message: t('No se ha podido importar el archivo'), type: 'error' })
  }

  useWebsocketAction({ websocketUrl: importWebsocketUrl, onComplete: onImportCompleted, onError: onImportFailed })

  const onCancelImport = () => {
    importCancelledRef.current = true
    setImportWebsocketUrl(null)
    setImportInProgress(false)
  }

  const onImportInventoryFile = async () => {
    importCancelledRef.current = false
    importStartedAtRef.current = Date.now()
    setImportCompleted(false)
    setImportInProgress(true)

    try {
      const report = await importInventoryFile({ storeId, file: inventoryFile })

      if (importCancelledRef.current) return

      importReportIdRef.current = report.id
      setImportWebsocketUrl(report.websocket_url)
    } catch (error) {
      setImportInProgress(false)

      if (importCancelledRef.current) return

      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const onCloseImportResultModal = () => {
    setShowImportResultModal(false)

    if (importResult.file_error) return

    closeImportInventoryDataDrawer()
    refetchItems()
    fetchInventoryMetrics()
  }

  return {
    openImportInventoryDataDrawer,
    closeImportInventoryDataDrawer,
    showImportInventoryDataDrawer,
    onSelectInventoryFile,
    onDeleteInventoryFile,
    inventoryFile,
    setInventoryFile,
    inventoryError,
    setInventoryError,
    exportEmails,
    setExportEmails,
    exportTemplate,
    setExportTemplate,
    exportFormat,
    setExportFormat,
    onExportInventoryItems,
    onImportInventoryFile,
    onCancelImport,
    importInProgress,
    importCompleted,
    uploading,
    exportedInventory,
    exportingInventory,
    importResult,
    showImportResultModal,
    onCloseImportResultModal,
  }
}

export default useImportInventoryData
